import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import {
  ArrowLeft,
  ArrowUpRight,
  BookOpen,
  Building2,
  CheckCircle2,
  Clock3,
  Lightbulb,
  Sparkles,
  Target,
  TrendingUp,
} from 'lucide-react';

import Navbar from '../components/Navbar';
import Popup from '../components/Popup';
import ProcessingAnimation from '../components/ProcessingAnimation';
import './Cases.css';

const detailSections = [
  { key: 'companyBackground', label: 'Company background', eyebrow: 'Context', icon: Building2 },
  { key: 'problemStatement', label: 'Problem statement', eyebrow: 'The challenge', icon: BookOpen, featured: true },
  { key: 'companyFinancialBusinessInformation', label: 'Financial & business information', eyebrow: 'Business facts', icon: TrendingUp },
  { key: 'keyDetailsIndustry', label: 'Industry details', eyebrow: 'Market context', icon: CheckCircle2 },
];

const difficultyDetails = (value) => {
  const normalized = String(value || 'Medium').trim().toLowerCase();
  if (['easy', 'beginner'].includes(normalized)) return { className: 'is-easy', time: '12 min' };
  if (['hard', 'advanced'].includes(normalized)) return { className: 'is-hard', time: '30 min' };
  return { className: 'is-medium', time: '20 min' };
};

const CaseStudyDetail = () => {
  const { id } = useParams();
  const { getToken } = useAuth();
  const url = process.env.REACT_APP_BACK_END_URL;

  const [caseData, setCaseData] = useState(null);
  const [isCaseLoading, setIsCaseLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);

  const fetchCase = useCallback(async (signal) => {
    setIsCaseLoading(true);
    setFetchError('');
    setCaseData(null);
    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/case/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal,
      });
      if (!response.ok) throw new Error('This case could not be loaded.');
      const data = await response.json();
      setCaseData(data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error loading case:', error);
        setFetchError('We could not open this case. It may have moved, or the connection was interrupted.');
      }
    } finally {
      if (!signal?.aborted) setIsCaseLoading(false);
    }
  }, [getToken, id, url]);

  useEffect(() => {
    const controller = new AbortController();
    fetchCase(controller.signal);
    return () => controller.abort();
  }, [fetchCase]);

  const difficulty = useMemo(
    () => difficultyDetails(caseData?.difficultyLevel),
    [caseData?.difficultyLevel]
  );

  const wordCount = useMemo(() => {
    const trimmed = answer.trim();
    return trimmed ? trimmed.split(/\s+/).length : 0;
  }, [answer]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedAnswer = answer.trim();
    if (!trimmedAnswer) {
      setSubmitError('Add your recommendation before requesting feedback.');
      return;
    }

    setSubmitError('');
    setIsSubmitting(true);
    try {
      const token = await getToken();
      const response = await fetch(`${url}/api/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ caseId: id, answer: trimmedAnswer }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Your response could not be graded.');

      setFeedback(data.feedback || { grade: data.grade || '' });
      setIsSubmitting(false);
      setShowFeedback(true);
    } catch (error) {
      console.error('Error submitting case:', error);
      setSubmitError(error.message || 'Something went wrong while grading your response. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="case-workspace">
      <Navbar />
      <ProcessingAnimation isLoading={isSubmitting} />

      {isCaseLoading ? (
        <main className="case-workspace-shell case-detail-state" aria-live="polite">
          <div className="case-detail-skeleton is-title" />
          <div className="case-detail-skeleton-grid">
            <div className="case-detail-skeleton is-brief" />
            <div className="case-detail-skeleton is-response" />
          </div>
          <span className="sr-only">Loading case workspace</span>
        </main>
      ) : fetchError || !caseData ? (
        <main className="case-workspace-shell case-detail-state is-error">
          <span className="case-kicker"><Sparkles size={16} /> Case workspace</span>
          <h1>This case is unavailable.</h1>
          <p>{fetchError}</p>
          <div>
            <button type="button" onClick={() => fetchCase()}>Try again</button>
            <Link to="/cases"><ArrowLeft size={17} /> Back to cases</Link>
          </div>
        </main>
      ) : (
        <>
          <header className="case-detail-hero">
            <div className="case-workspace-shell case-detail-hero-inner">
              <Link to="/cases" className="case-back-link"><ArrowLeft size={17} /> Case library</Link>
              <div className="case-detail-heading">
                <div>
                  <span className="case-kicker"><Sparkles size={16} /> Interactive case workspace</span>
                  <h1>{caseData.title}</h1>
                </div>
                <p>Read the brief, structure your thinking, and submit a clear recommendation for focused AI feedback.</p>
              </div>
              <div className="case-detail-meta">
                <span>{caseData.area || 'Consulting case'}</span>
                <span className={`case-difficulty ${difficulty.className}`}>{caseData.difficultyLevel || 'Medium'}</span>
                <span><Clock3 size={15} /> {difficulty.time} estimate</span>
              </div>
            </div>
          </header>

          <main className="case-workspace-shell case-detail-main">
            <div className="case-detail-layout">
              <article className="case-dossier">
                <div className="case-dossier-header">
                  <div><span>Case brief</span><strong>Read before responding</strong></div>
                  <BookOpen size={24} />
                </div>
                {detailSections.map((section, index) => {
                  const Icon = section.icon;
                  return (
                    <section key={section.key} className={`case-dossier-section ${section.featured ? 'is-featured' : ''}`}>
                      <div className="case-dossier-section-head">
                        <span>{String(index + 1).padStart(2, '0')}</span>
                        <div>
                          <small>{section.eyebrow}</small>
                          <h2><Icon size={21} /> {section.label}</h2>
                        </div>
                      </div>
                      <p>{caseData[section.key] || 'No additional details were provided for this section.'}</p>
                    </section>
                  );
                })}
              </article>

              <aside className="case-response-panel">
                <div className="case-response-heading">
                  <span>Response workbench</span>
                  <Target size={24} />
                </div>
                <div className="case-objective">
                  <small>Your objective</small>
                  <h2>Build your recommendation.</h2>
                  <p>{caseData.objective}</p>
                </div>

                <form onSubmit={handleSubmit}>
                  <label htmlFor="case-answer">Your response</label>
                  <textarea
                    id="case-answer"
                    name="answer"
                    value={answer}
                    onChange={(event) => {
                      setAnswer(event.target.value);
                      if (submitError) setSubmitError('');
                    }}
                    placeholder="Structure your approach, explain the supporting logic, and finish with a clear recommendation…"
                    disabled={isSubmitting}
                    required
                  />
                  <div className="case-answer-meta">
                    <span>{wordCount} {wordCount === 1 ? 'word' : 'words'}</span>
                    <span>Saved here while you work</span>
                  </div>
                  {submitError && <p className="case-submit-error" role="alert">{submitError}</p>}
                  <button type="submit" disabled={isSubmitting || !answer.trim()}>
                    {isSubmitting ? 'Reviewing response…' : 'Get AI feedback'} <ArrowUpRight size={18} />
                  </button>
                </form>

                <div className="case-response-guide">
                  <span><Lightbulb size={17} /> A strong response usually:</span>
                  <ol>
                    <li><b>01</b> Frames the core question</li>
                    <li><b>02</b> Uses facts from the brief</li>
                    <li><b>03</b> Lands on a recommendation</li>
                  </ol>
                </div>
              </aside>
            </div>
          </main>
        </>
      )}

      <Popup open={showFeedback} onClose={() => setShowFeedback(false)} feedback={feedback} />
    </div>
  );
};

export default CaseStudyDetail;
